import SaveAndCloseOrAddButton from "@app/components/save-and-close-or-add-button.tsx";
import useSetupStateCodeData from "@app/data-hooks/setup/use-setup-state-code-data.ts";
import useCategoryData from "@app/data-hooks/use-category-data.ts";
import useClientData from "@app/data-hooks/use-client-data.ts";
import { Box, Button, Checkbox, FormControlLabel, Grid2, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { QueryStatusRouter, Select } from "bi-lib";
import { useRef } from "react";

interface Props {
    id?: number;
}

export default function ClientForm({ id }: Props) {
    const { client, saveClient } = useClientData({ clientId: id });
    const { clientCategories } = useCategoryData();
    const { stateCodes } = useSetupStateCodeData();

    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            clientName: client.data?.clientName,
            clientNameShort: client.data?.clientNameShort,
            clientNumber: client.data?.clientNumber,
            clientCategoryId: client.data?.clientCategoryId,
            address1: client.data?.address?.address1,
            address2: client.data?.address?.address2,
            city: client.data?.address?.city,
            stateCodeId: client.data?.address?.stateCodeId,
            postalCode: client.data?.address?.postalCode,
            phone: client.data?.phone,
            fax: client.data?.fax,
            notes: client.data?.notes,
            receiptRequired: client.data?.receiptRequired,
            isActive: client.data?.active,
        },
        onSubmit: async ({ value }) => {
            await saveClient.mutateAsync({
                clientId: id,
                clientName: value.clientName,
                clientNameShort: value.clientNameShort,
                clientNumber: value.clientNumber,
                clientCategoryId: value.clientCategoryId,
                address: {
                    address1: value.address1,
                    address2: value.address2,
                    city: value.city,
                    stateCodeId: value.stateCodeId,
                    postalCode: value.postalCode,
                },
                phone: value.phone,
                fax: value.fax,
                notes: value.notes,
                receiptRequired: value.receiptRequired,
                isActive: value.isActive,
            });
        },
    });

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
            ref={formRef}
        >
            <QueryStatusRouter queryHook={client}>
                <Grid2 container spacing={2} mt={2}>
                    <Grid2 size={6}>
                        <form.Field name={"clientName"}>
                            {(field) => (
                                <TextField
                                    label={"Client Name"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"clientNameShort"}>
                            {(field) => (
                                <TextField
                                    label={"Short Name"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"clientNumber"}>
                            {(field) => (
                                <TextField
                                    label={"Client Number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    required
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"clientCategoryId"}>
                            {(field) => (
                                <QueryStatusRouter queryHook={clientCategories}>
                                    <Select
                                        label={"Category"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        fullWidth
                                        options={clientCategories.data?.map((x) => ({
                                            label: x.clientCategory,
                                            value: x.clientCategoryId,
                                        }))}
                                        required
                                    />
                                </QueryStatusRouter>
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"address1"}>
                            {(field) => (
                                <TextField
                                    label={"Address 1"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"address2"}>
                            {(field) => (
                                <TextField
                                    label={"Address 2"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"city"}>
                            {(field) => (
                                <TextField
                                    label={"City"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"stateCodeId"}>
                            {(field) => (
                                <QueryStatusRouter queryHook={stateCodes}>
                                    <Select
                                        label={"State"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        fullWidth
                                        options={stateCodes.data?.map((x) => ({
                                            label: x.stateName,
                                            value: x.stateCodeId,
                                        }))}
                                    />
                                </QueryStatusRouter>
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"postalCode"}>
                            {(field) => (
                                <TextField
                                    label={"Postal Code"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"phone"}>
                            {(field) => (
                                <TextField
                                    label={"Work Phone"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"fax"}>
                            {(field) => (
                                <TextField
                                    label={"Fax Number"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={12}>
                        <form.Field name={"notes"}>
                            {(field) => (
                                <TextField
                                    label={"Notes"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    multiline
                                    minRows={3}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={12}>
                        <form.Field name={"receiptRequired"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Receipt Required"}
                                />
                            )}
                        </form.Field>
                        <form.Field name={"isActive"}>
                            {(field) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.state.value ?? false}
                                            onChange={(e) => field.handleChange(e.target.checked)}
                                        />
                                    }
                                    label={"Active"}
                                />
                            )}
                        </form.Field>
                    </Grid2>
                </Grid2>
                <Box display={"flex"} gap={1} mt={1}>
                    <Button variant={"outlined"} onClick={() => navigate({ to: ".." })}>
                        Cancel
                    </Button>
                    <SaveAndCloseOrAddButton
                        onSave={form.handleSubmit}
                        onSecondary={
                            id == undefined
                                ? () => void navigate({ to: "/client/create" })
                                : () => void navigate({ to: ".." })
                        }
                        secondaryActionTitle={id == undefined ? "Add New" : "Close"}
                        formRef={formRef}
                    />
                </Box>
            </QueryStatusRouter>
        </form>
    );
}
